import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type {
  TeamUpdate,
  TeamMemberUpdate,
} from '../types';

// Query keys
export const TEAMS_QUERY_KEY = 'teams';
export const TEAM_QUERY_KEY = 'team';

export const useTeams = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // Get all teams for the current user
  const {
    data: teams = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: [TEAMS_QUERY_KEY],
    queryFn: teamsAPI.getAll,
    enabled: isAuthenticated, // Only fetch when authenticated
    staleTime: 30000, // 30 seconds
  });

  // Create team mutation
  const createTeamMutation = useMutation({
    mutationFn: teamsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });

  // Update team mutation
  const updateTeamMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TeamUpdate }) => 
      teamsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });

  // Delete team mutation
  const deleteTeamMutation = useMutation({
    mutationFn: teamsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });

  // Regenerate join code mutation
  const regenerateJoinCodeMutation = useMutation({
    mutationFn: teamsAPI.regenerateJoinCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });

  // Join team mutation
  const joinTeamMutation = useMutation({
    mutationFn: teamsAPI.joinTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });

  // Update team member mutation
  const updateMemberMutation = useMutation({
    mutationFn: ({ teamId, memberId, data }: { 
      teamId: string; 
      memberId: string; 
      data: TeamMemberUpdate 
    }) => teamsAPI.updateMember(teamId, memberId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });

  // Remove team member mutation
  const removeMemberMutation = useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: string; memberId: string }) => 
      teamsAPI.removeMember(teamId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });

  return {
    // Data
    teams,
    isLoading,
    error,
    
    // Actions
    refetch,
    createTeam: createTeamMutation.mutateAsync,
    updateTeam: updateTeamMutation.mutateAsync,
    deleteTeam: deleteTeamMutation.mutateAsync,
    regenerateJoinCode: regenerateJoinCodeMutation.mutateAsync,
    joinTeam: joinTeamMutation.mutateAsync,
    updateMember: updateMemberMutation.mutateAsync,
    removeMember: removeMemberMutation.mutateAsync,
    
    // Loading states
    isCreating: createTeamMutation.isPending,
    isUpdating: updateTeamMutation.isPending,
    isDeleting: deleteTeamMutation.isPending,
    isRegeneratingCode: regenerateJoinCodeMutation.isPending,
    isJoining: joinTeamMutation.isPending,
    isUpdatingMember: updateMemberMutation.isPending,
    isRemovingMember: removeMemberMutation.isPending,
  };
};

// Hook for getting a specific team
export const useTeam = (teamId: string | null) => {
  return useQuery({
    queryKey: [TEAM_QUERY_KEY, teamId],
    queryFn: () => teamId ? teamsAPI.getById(teamId) : null,
    enabled: !!teamId,
    staleTime: 30000,
  });
}; 